import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { galleryApi } from '../services/api';
import { ApiError } from '../services/api';
import type { GalleryTheme } from '../types';
import ThemeList from '../components/ThemeList';

export default function YearPage() {
  const { year } = useParams<{ year: string }>();
  const [themes, setThemes] = useState<GalleryTheme[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invalidYear, setInvalidYear] = useState(false);

  const fetchThemes = () => {
    if (!year) return;
    setError(null);
    setThemes(null);
    setInvalidYear(false);
    galleryApi
      .getThemes(year)
      .then((res) => setThemes(res.themes))
      .catch((err) => {
        if (err instanceof ApiError && (err.status === 400 || err.status === 404)) {
          setInvalidYear(true);
        } else {
          setError('Failed to load themes. Please try again.');
        }
      });
  };

  useEffect(() => {
    fetchThemes();
  }, [year]);

  if (invalidYear) {
    return <Navigate to="/" replace />;
  }

  const helmet = (
    <Helmet>
      <title>{`FWO Photo Gallery - ${year}`}</title>
      <meta name="description" content={`Browse published competition photos from FlyWideOpen ${year}`} />
      <meta property="og:site_name" content="FlyWideOpen Photo Gallery" />
      <meta property="og:title" content={`FWO Photo Gallery - ${year}`} />
      <meta property="og:description" content={`Browse published competition photos from FlyWideOpen ${year}`} />
      <meta property="og:url" content={window.location.href} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`FWO Photo Gallery - ${year}`} />
      <meta name="twitter:description" content={`Browse published competition photos from FlyWideOpen ${year}`} />
    </Helmet>
  );

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        {helmet}
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchThemes}
          className="rounded-md bg-fwo-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (themes === null) {
    return (
      <div className="flex items-center justify-center py-20">
        {helmet}
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-fwo-blue border-t-transparent" />
      </div>
    );
  }

  if (themes.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-center">
        {helmet}
        <p className="text-gray-500">No published themes for this year</p>
      </div>
    );
  }

  return (
    <div>
      {helmet}
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{year} Gallery</h1>
      <ThemeList themes={themes} year={year!} />
    </div>
  );
}
